import { Skeleton, SxProps, Theme, Typography } from '@mui/material'
import Grid from '@mui/material/Grid2'
import { ADD_PATIENT, LOGIN } from 'navigation/CONSTANTS'
import PatientCard from 'components/PatientCard'
import InputBase from 'design-system/input/base'
import { Navigate } from 'react-router-dom'
import Storage from 'storage/auth'
import { getAge } from 'utils/date'
import ButtonBase from 'design-system/button'
import { AddOutlined } from '@mui/icons-material'
import NavLinkBase from 'design-system/NavLink'
import { StyledTabs, StyledTab } from 'components/TabNavigator'
import { adjustColorForTheme } from 'styles/utils'
import { usePatients } from './usePatients'
import SelectBox from 'components/SelectBox'
import { sortOptions } from './utils'
import { IPatientResponse } from 'types/patient/types'

const PatientCardComponent: React.FC<{ patient: IPatientResponse; sx?: SxProps<Theme> }> = ({ patient, sx }) => (
  <Grid
    key={`patient-${patient.patient_id}`}
    size={{ sm: 12, md: 6 }}
  >
    <PatientCard
      patientId={patient.patient_id}
      name={patient.first_name + ' ' + patient.last_name}
      email={patient.email}
      age={
        patient.birth_date ? getAge(patient.birth_date) : null
      }
      imageUrl={patient.user_image}
      overallHealth={patient.overall_calculation}
      overallHealthWarning={patient.overall_calculation_warning}
      overallHealthColor={patient.overall_calculation_color}
      vitalSigns={patient.patient_vital_signs}
      milestones={patient.patient_milestones}
      sx={sx}
    />
  </Grid>);

const PatientsPage = () => {
  // storage
  const user = Storage.user.get()

  // Custom hook with all business logic
  const {
    state,
    setFilter,
    activePatients,
    pendingWatchReturnPatients,
    patientLength,
    selectedIndex,
    handleChange,
    notAllowedMonitoring,
    getErrorMessage,
    isHDA,
    currentList,
    sortBy,
    setSortBy
  } = usePatients()

  if (user === null) {
    return <Navigate to={LOGIN} />
  }

  const render = {
    idle: <></>,
    loading: (
      <>
        {[1, 2, 4, 5, 6].map(i => (
          <Grid key={i} size={12} display='flex' justifyContent='center'>
            <Skeleton key={i} variant='rectangular' width='100%' height={70} />
          </Grid>
        ))}
      </>
    ),
    success: (
      <>
        {patientLength === 0 && pendingWatchReturnPatients.length === 0 ? (
          <Grid size={12}>
            <Typography variant='body1'>Nenhum paciente encontrado</Typography>
          </Grid>
        ) : (
          <>
            {patientLength > 0 && (
              <>
                <Grid size={12} display='flex' justifyContent='flex-end'>
                  <Typography variant='body1'>
                    {patientLength} paciente{patientLength > 1 ? 's' : ''}
                  </Typography>
                </Grid>
                {activePatients.map((patient) => (
                  <PatientCardComponent key={`patient-${patient.patient_id}`} patient={patient} sx={[
                        currentList === 'active' && patient.outdated
                          ? {
                              backgroundColor: patient.outdated_48
                                ? 'outdatedCritical.main'
                                : 'outdated.main'
                            }
                          : {
                              backgroundColor: theme =>
                                adjustColorForTheme(
                                  theme.palette.mode,
                                  theme.palette.tabBorder.main,
                                  0.05
                                )
                            },
                        { marginBottom: '16px' }
                      ]} />
                ))}
              </>
            )}
            {pendingWatchReturnPatients.length > 0 && (
              <>
                <Grid size={12} sx={{ marginTop: '32px' }}>
                  <Typography variant='h6'>
                    Relógio pendente de devolução
                  </Typography>
                </Grid>
                <Grid size={12} display='flex' justifyContent='flex-end'>
                  <Typography variant='body1'>
                    {pendingWatchReturnPatients.length} paciente
                    {pendingWatchReturnPatients.length > 1 ? 's' : ''}
                  </Typography>
                </Grid>
                {pendingWatchReturnPatients.map((patient) => (
                  <PatientCardComponent key={`patient-${patient.patient_id}`} patient={patient} sx={{
                        backgroundColor: theme =>
                          adjustColorForTheme(
                            theme.palette.mode,
                            theme.palette.tabBorder.main,
                            0.05
                          ),
                        marginBottom: '16px'
                      }} />
                ))}
              </>
            )}
          </>
        )}
      </>
    ),
    error: (
      <Grid size={12}>
        {/* TODO: Criar um componente padrao de erro */}
        <Typography variant='body1'>
          {getErrorMessage()}
          {/* TODO: Add translation */}
        </Typography>
      </Grid>
    )
  }

  return (
    <Grid
      container
      spacing={2}
      p={2}
      margin='0 auto'
      width='100%'
      maxWidth={1200}
    >
      <Grid size={12} display='flex' justifyContent='space-between'>
        <Typography variant='h5'>Lista de Pacientes</Typography>
        {notAllowedMonitoring ? (
          <ButtonBase variant='text' disabled={notAllowedMonitoring}>
            <AddOutlined />
            <Typography variant='body1'>Adicionar</Typography>
          </ButtonBase>
        ) : (
          <NavLinkBase to={ADD_PATIENT} absolute>
            <ButtonBase variant='text'>
              <AddOutlined />
              <Typography variant='body1'>Adicionar</Typography>
            </ButtonBase>
          </NavLinkBase>
        )}
      </Grid>
      <Grid container size={12} display='flex' justifyContent='center'>
        <Grid size={12} spacing={3}>
          <InputBase
            disabled={notAllowedMonitoring}
            label='Digite para buscar por nome'
            onChange={e => setFilter(e.target.value.trim())}
          />
        </Grid>
      </Grid>
      <Grid container size={12} spacing={3}>
        {isHDA && (
          <StyledTabs
            value={selectedIndex}
            onChange={handleChange}
            variant='scrollable'
            scrollButtons='auto'
            aria-label='tab navigator'
            slotProps={{
              indicator: {
                sx: {
                  background: 'secondary.main'
                }
              }
            }}
          >
            {['Em estudo', 'Estudo finalizado', 'Estudo descartado'].map(
              (tab, index) => (
                <StyledTab
                  label={tab}
                  className={selectedIndex === index ? 'selected' : ''}
                  key={`tab-${tab}`}
                  sx={{ fontSize: '18px', paddingInline: '32px' }}
                />
              )
            )}
          </StyledTabs>
        )}
        {isHDA && currentList === 'active' && (
          <Grid
            container
            size={12}
            display='flex'
            justifyContent='flex-end'
            sx={{ mt: 2 }}
          >
            <Grid size={{ xs: 12, sm: 6, md: 4 }}>
              <SelectBox
                label='Ordenar por'
                menuItems={sortOptions}
                control={[sortBy, setSortBy]}
                fullWidth
              />
            </Grid>
          </Grid>
        )}
        {render[state]}
      </Grid>
    </Grid>
  )
}

export default PatientsPage
